import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Backdrop,
  CircularProgress,
  useTheme,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
  IconButton,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { Add, LockReset, Business, Star, Search, Visibility, Edit, Delete, Person, Email, Phone } from '@mui/icons-material';
import { User, Company } from 'modules/Interfaces/Company';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { UserDialog } from './UserDialog';
import { UserViewDialog } from './UserViewDialog';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { Logger } from 'utils/Logger';
import { apiClient } from 'utils/ApiClient';
import { GET_ALL_COMPANY_USERS_URL, GET_ALL_COMPANIES_URL, API_BASE_URL } from 'envConfig';
import { handleApiError } from 'utils/ErrorHandler';
import { LocalStorageAuth } from 'utils/LocalStorageAuth';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { CustomAction } from 'components/DataTable/DataTable.types';

// Schema object for user data - only including the columns we want to display
const userSchema = {
  paths: {
    email: { instance: 'String' },
    mobile: { instance: 'String' },
    status: { instance: 'String' },
    companyName: { instance: 'String' },
    name: { instance: 'String' },
    role: { instance: 'String' },
    moduleAndRole: { instance: 'Array' }
  }
};

// Static column renderers that don't need company data
const createColumnRenderers = () => ({
  profile: (row: User) => {
    if (!row.profile) return 'No profile';
    const { firstName, lastName, city, country } = row.profile;
    const name = `${firstName || ''} ${lastName || ''}`.trim();
    const location = [city, country].filter(Boolean).join(', ');
    return `${name}${location ? ` (${location})` : ''}`;
  },
  status: (row: User) => {
    const status = row.status?.toLowerCase() || '';
    switch (status) {
      case 'active':
        return <Chip label="ACTIVE" color="success" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'inactive':
        return <Chip label="INACTIVE" color="error" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'pending':
        return <Chip label="PENDING" color="warning" size="small" sx={{ fontWeight: 'bold' }} />;
      default:
        return <Chip label={status.toUpperCase() || 'UNKNOWN'} color="default" size="small" sx={{ fontWeight: 'bold' }} />;
    }
  },
});

export const Users: React.FC = () => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<Partial<User> | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState<boolean>(false);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [userToView, setUserToView] = useState<User | null>(null);

  const VIEWMODE_STORAGE_KEY = 'users-view-mode';
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [usersData, setUsersData] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const TILE_PAGE_SIZE = 24;
  const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);

  // Get current user from Redux store
  const currentUserFromStore = useSelector((state: RootState) => state.authentication.user);

  // Create column renderers - memoized to prevent infinite loops
  const columnRenderers = useMemo(() => ({
    ...createColumnRenderers(),
    companyName: (row: User) => getCompanyName(row.companyId),
    name: (row: User) => {
      if (!row.profile) return 'No profile';
      const { firstName, lastName, city, country } = row.profile;
      const name = `${firstName || ''} ${lastName || ''}`.trim();
      const location = [city, country].filter(Boolean).join(', ');
      return `${name}${location ? ` (${location})` : ''}`;
    },
    role: (row: User) => {
      const primaryModules = row.moduleAndRole || [];
      const additionalMemberships = row.memberOfCompanies || [];
      const hasPrimary = primaryModules.length > 0;
      const hasAdditional = additionalMemberships.some(m => m.moduleAndRole?.length > 0);

      if (!hasPrimary && !hasAdditional) {
        return <Chip label="No Modules" color="default" size="small" sx={{ fontWeight: 'bold' }} />;
      }

      const renderModuleChips = (modules: Array<{ module: string; role: string }>) =>
        modules.map((mr, idx) => (
          <Chip key={idx} label={`${mr.module}: ${mr.role}`} size="small"
            color={mr.role === 'admin' ? 'warning' : 'default'}
            sx={(t) => ({
              fontWeight: 'bold',
              ...(mr.role !== 'admin' && {
                backgroundColor: t.palette.mode === 'dark' ? t.palette.grey[700] : t.palette.grey[300],
                color: t.palette.mode === 'dark' ? t.palette.common.white : t.palette.common.black,
              }),
            })} />
        ));

      const showCompanyHeaders = hasAdditional || additionalMemberships.length > 0;

      if (!showCompanyHeaders && hasPrimary) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {renderModuleChips(primaryModules)}
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {hasPrimary && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <Business sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {getCompanyName(row.companyId)}
                </Typography>
                <Tooltip title="Primary company">
                  <Star sx={{ fontSize: 12, color: 'warning.main' }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pl: 2.25 }}>
                {renderModuleChips(primaryModules)}
              </Box>
            </Box>
          )}
          {additionalMemberships.map((membership, mIdx) => {
            const mods = membership.moduleAndRole || [];
            if (mods.length === 0) return null;
            return (
              <Box key={mIdx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Business sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {getCompanyName(membership.companyId)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pl: 2.25 }}>
                  {renderModuleChips(mods)}
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    },
  }), [companies]); // Only recreate when companies change

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await apiClient.get('/company/all');
        const companiesData = Array.isArray(data) ? data : (data.data || []);
        setCompanies(companiesData);
      } catch (error) {
        Logger.error('Failed to fetch companies:', error);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch users data for tile view
  const fetchUsersData = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await apiClient.get(GET_ALL_COMPANY_USERS_URL.replace(API_BASE_URL, ''));
      const users = Array.isArray(data) ? data : (data.data || []);
      setUsersData(users);
    } catch (error) {
      Logger.error('Failed to fetch users for tile view:', error);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsersData();
  }, [refreshTrigger, fetchUsersData]);

  // Function to get company name by ID
  const getCompanyName = (companyId: string): string => {
    if (!companyId) return 'No Company';
    
    const company = companies.find(c => c._id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  // Custom data processor for the data table - memoized to prevent infinite loops
  const customDataProcessor = useCallback((data: User[]) => {
    return data.map(user => ({
      ...user,
      // Add computed fields for filtering
      companyName: getCompanyName(user.companyId),
      name: user.profile ? 
        `${user.profile.firstName || ''} ${user.profile.lastName || ''} ${user.profile.city || ''} ${user.profile.country || ''}`.trim().toLowerCase() : 
        'no profile'
    }));
  }, [companies]); // Only recreate when companies change

  const filteredUsers = useMemo(() => {
    let filtered = usersData;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(u => {
        const name = `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        const mobile = (u.mobile || '').toLowerCase();
        const company = getCompanyName(u.companyId).toLowerCase();
        const status = (u.status || '').toLowerCase();
        return name.includes(q) || email.includes(q) || mobile.includes(q) || company.includes(q) || status.includes(q);
      });
    }
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        const nameA = `${a.profile?.firstName || ''} ${a.profile?.lastName || ''}`.toLowerCase();
        const nameB = `${b.profile?.firstName || ''} ${b.profile?.lastName || ''}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortField === 'updatedAt') {
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        comparison = dateA - dateB;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return filtered;
  }, [usersData, searchTerm, companies, sortField, sortOrder]);

  useEffect(() => {
    setTileVisibleCount(TILE_PAGE_SIZE);
  }, [searchTerm, usersData, sortField, sortOrder]);

  const paginatedUsers = useMemo(() => filteredUsers.slice(0, tileVisibleCount), [filteredUsers, tileVisibleCount]);
  const hasMoreTiles = tileVisibleCount < filteredUsers.length;

  const [profileImageUrls, setProfileImageUrls] = useState<Record<string, string>>({});
  const fetchingImageIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (viewMode !== 'tile' || paginatedUsers.length === 0) return;
    const token = LocalStorageAuth.getToken() || '';
    const activeCompanyId = LocalStorageAuth.getActiveCompanyId();
    const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` };
    if (activeCompanyId) headers['X-Active-Company-Id'] = activeCompanyId;

    paginatedUsers.forEach((u) => {
      if (!u.profile?.hasProfileImage) return;
      if (profileImageUrls[u._id] || fetchingImageIds.current.has(u._id)) return;
      fetchingImageIds.current.add(u._id);
      fetch(`${API_BASE_URL}/user/${u._id}/profile-image`, { headers })
        .then((resp) => {
          if (resp.ok) return resp.blob();
          return null;
        })
        .then((blob) => {
          if (blob) {
            setProfileImageUrls((prev) => ({ ...prev, [u._id]: URL.createObjectURL(blob) }));
          }
        })
        .catch(() => {})
        .finally(() => { fetchingImageIds.current.delete(u._id); });
    });
  }, [viewMode, paginatedUsers, profileImageUrls]);

  const handleEdit = (record: User): void => {
    setCurrentUser(record);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDelete = (record: User): void => {
    setUserToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleResetPassword = (record: User): void => {
    setUserToResetPassword(record);
    setResetPasswordDialogOpen(true);
  };

  const handleView = (record: User): void => {
    setUserToView(record);
    setViewDialogOpen(true);
  };

  // Custom actions function for the data table - memoized to prevent infinite loops
  const customActions = useCallback((_record: User): CustomAction[] => [
    {
      label: 'View Details',
      onClick: handleView,
      color: 'info',
      icon: <Visibility />
    },
    {
      label: 'Reset Password',
      onClick: handleResetPassword,
      color: 'warning',
      icon: <LockReset />
    }
  ], []); // Empty dependency array since handlers are stable

  // Function to check if delete should be disabled - memoized to prevent infinite loops
  const isDeleteDisabled = useCallback((record: User): boolean => {
    // Disable delete for current user
    if (currentUserFromStore?._id === record._id) {
      return true;
    }
    
    // Disable delete for users in system company
    if (record.companyId === currentUserFromStore?.companyId && 
        currentUserFromStore?.companyId === 'system') {
      return true;
    }
    
    return false;
  }, [currentUserFromStore?._id, currentUserFromStore?.companyId]);

  // Function to provide tooltip for disabled delete button - memoized to prevent infinite loops
  const deleteDisabledTooltip = useCallback((record: User): string => {
    if (currentUserFromStore?._id === record._id) {
      return "Cannot delete your own account";
    }
    
    if (record.companyId === currentUserFromStore?.companyId && 
        currentUserFromStore?.companyId === 'system') {
      return "Cannot delete system company users";
    }
    
    return "Delete user";
  }, [currentUserFromStore?._id, currentUserFromStore?.companyId]);

  const openNewUserDialog = (): void => {
    setCurrentUser({
      email: '',
      mobile: '',
      status: 'ACTIVE',
      moduleAndRole: [],
      profile: {
        firstName: '',
        lastName: '',
        avatar: 'https://www.gravatar.com/avatar',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        zip: '',
        termsAndConditions: false,
        privacyPolicy: false,
        emailNotifications: false,
        smsNotifications: false,
        whatsAppNotifications: false,
        dob: new Date()
      },
      companyId: currentUserFromStore?.companyId || ''
    });
    setIsEditing(false);
    setDialogOpen(true);
    setSaveLoading(false); // Reset loading state when opening new dialog
  };

  const confirmDelete = async (): Promise<void> => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      await apiClient.delete(`/user/${userToDelete._id}`);

      Logger.debug('User deleted successfully:', userToDelete._id);
      
      // Close dialog and refresh table
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      Logger.error('Failed to delete user:', errorMessage);
      // TODO: Show error message to user
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeDeleteDialog = (): void => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const closeDialog = (): void => {
    setDialogOpen(false);
    setCurrentUser(null);
    setIsEditing(false);
    setSaveLoading(false); // Reset loading state when closing dialog
  };

  const saveUser = async (userData: Partial<User>): Promise<void> => {
    if (!userData) return;

    try {
      setSaveLoading(true);
      
      // Determine companyId based on user role
      const companyId = userData.companyId || currentUserFromStore?.companyId || '';
      
      const url = isEditing ? `${API_BASE_URL}/user/${currentUser?._id}` : `${API_BASE_URL}/user/new/${companyId}`;
      const method = isEditing ? 'PATCH' : 'POST';
      
      // Prepare payload based on whether we're editing or creating
      let payloadToSend: any;
      
      if (isEditing) {
        // For editing, remove _id and authentication
        const { _id, authentication, companyId: _, ...payload } = userData;
        payloadToSend = {
          ...payload,
          // role field removed - now handled through moduleAndRole
          status: payload.status?.toLowerCase()
        };
      } else {
        // For new users, include password and remove _id
        const { _id, companyId: _, ...userWithoutId } = userData;
        payloadToSend = {
          ...userWithoutId,
          // role field removed - now handled through moduleAndRole
          status: userWithoutId.status?.toLowerCase()
        };
      }
      
      // Debug: Log the payload being sent
      Logger.debug(`Sending payload for ${isEditing ? 'update' : 'create'}:`, payloadToSend);
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      if (isEditing) {
        await apiClient.patch(`/user/${currentUser?._id}`, {
          ...payloadToSend,
          modifiedBy: currentUserFromStore?.email,
        });
      } else {
        await apiClient.post(`/user/new/${companyId}`, {
          ...payloadToSend,
          createdBy: currentUserFromStore?.email,
        });
      }

      Logger.debug(`User ${isEditing ? 'updated' : 'created'} successfully:`, userData);
      
      // Close dialog and refresh table
      closeDialog();
      setRefreshTrigger(prev => prev + 1);
      setSaveLoading(false);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      Logger.error(`Failed to ${isEditing ? 'update' : 'create'} user:`, errorMessage);
      setSaveLoading(false);
      throw error; // Re-throw the error to be handled by the dialog
    }
  };

  const resetPassword = async (): Promise<{ password: string } | null> => {
    if (!userToResetPassword) return null;

    try {
      setResetPasswordLoading(true);
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const result = await apiClient.post(`/user/resetpassword/${userToResetPassword._id}`);
      Logger.debug('Password reset successfully:', result);
      
      // Refresh table
      setRefreshTrigger(prev => prev + 1);
      
      // Return the password from the response - extract from data.password
      return { password: result.data.password };
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      Logger.error('Failed to reset password:', errorMessage);
      throw error; // Re-throw to let the dialog handle the error
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const closeResetPasswordDialog = (): void => {
    setResetPasswordDialogOpen(false);
    setUserToResetPassword(null);
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
        }}
      >
        {/* Mobile: Search toggle */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileSearchOpen(prev => !prev)}
            size="large"
            sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }}
          >
            <Search />
          </IconButton>
        </Box>

        {/* Desktop: Search Box */}
        <Box sx={{ display: { xs: 'none', sm: 'block' }, flexGrow: 1 }}>
          <TextField
            label="Search Users"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '200px' }}
          />
        </Box>

        {/* Spacer for mobile */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexGrow: 1 }} />

        {/* Desktop: Sort Controls */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Sort By</InputLabel>
            <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as string)}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="updatedAt">Last Updated On</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel>Order</InputLabel>
            <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as string)}>
              <MenuItem value="asc">ASC</MenuItem>
              <MenuItem value="desc">DSC</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Desktop: View Mode Toggle + New User */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
                localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '&:hover': { backgroundColor: 'secondary.dark' },
                },
              },
            }}
          >
            <ToggleButton value="tile" aria-label="Tile view">
              <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={openNewUserDialog}
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            New User
          </Button>
        </Box>

        {/* Mobile: New User icon button */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={openNewUserDialog}
            size="large"
            sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }}
          >
            <Add />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile: Expandable search field */}
      {mobileSearchOpen && (
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 2 }}>
          <TextField
            label="Search Users"
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>Sort By</InputLabel>
              <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as string)}>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="updatedAt">Last Updated On</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>Order</InputLabel>
              <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as string)}>
                <MenuItem value="asc">ASC</MenuItem>
                <MenuItem value="desc">DSC</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {/* ─── TABLE VIEW ─── */}
      {viewMode === 'table' && (
        <DataTableWithStaticSchema
          dataUrl={GET_ALL_COMPANY_USERS_URL}
          schemaObject={userSchema}
          columnsToExclude={['moduleAndRole']}
          onEdit={handleEdit}
          onDelete={handleDelete}
          customActions={customActions}
          columnRenderers={columnRenderers}
          refreshTrigger={refreshTrigger}
          isDeleteDisabled={isDeleteDisabled}
          deleteDisabledTooltip={deleteDisabledTooltip}
          customDataProcessor={customDataProcessor}
          columnsWithDisabledFilter={['role']}
          externalSearchQuery={searchTerm}
          hideSearch
        />
      )}

      {/* ─── TILE VIEW ─── */}
      {viewMode === 'tile' && (
        usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No users match your search.' : 'No users found.'}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2.5}>
          <Box display="flex" flexWrap="wrap" gap={2.5} justifyContent="normal">
            {paginatedUsers.map((user) => {
              const fullName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || 'No Name';
              const initials = `${(user.profile?.firstName || '?')[0]}${(user.profile?.lastName || '?')[0]}`.toUpperCase();
              const statusLower = user.status?.toLowerCase() || '';
              const statusColor: 'success' | 'warning' | 'error' | 'default' =
                statusLower === 'active' ? 'success' : statusLower === 'pending' ? 'warning' : statusLower === 'inactive' ? 'error' : 'default';
              const companyName = getCompanyName(user.companyId);
              const isSelf = currentUserFromStore?._id === user._id;
              const avatarSrc = profileImageUrls[user._id]
                || (user.profile?.avatar && !user.profile.avatar.includes('gravatar.com/avatar') ? user.profile.avatar : undefined);

              return (
                <Box
                  key={user._id}
                  onClick={() => handleView(user)}
                  sx={{
                    width: 260,
                    height: 200,
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover .tile-overlay': { height: 130 },
                    '&:hover .tile-details': { opacity: 1 },
                    '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                    '@media (hover: none)': {
                      '& .tile-overlay': { height: 130 },
                      '& .tile-details': { opacity: 1 },
                      '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                    },
                  }}
                >
                  {/* Gradient background with avatar and name */}
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  }}>
                    <Avatar
                      src={avatarSrc}
                      sx={{ width: 72, height: 72, fontSize: '1.5rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', border: '2px solid rgba(255,255,255,0.4)' }}
                    >
                      {initials}
                    </Avatar>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#fff', lineHeight: 1.2, textShadow: '0 1px 4px rgba(0,0,0,0.3)', textAlign: 'center', px: 1 }} noWrap>
                      {fullName}
                    </Typography>
                    {user.mobile && (
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', textAlign: 'center', px: 1 }} noWrap>
                        {user.mobile}
                      </Typography>
                    )}
                  </Box>

                  {/* Top-right: Status badge */}
                  <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                    <Chip
                      label={user.status?.toUpperCase() || 'UNKNOWN'}
                      size="small"
                      color={statusColor}
                      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                    />
                  </Box>

                  {/* Bottom overlay — slides up on hover */}
                  <Box
                    className="tile-overlay"
                    sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      overflow: 'hidden',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                      transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      height: 40, px: 1.5,
                      display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                    }}
                  >
                    {/* Email — truncated in default state */}
                    <Typography variant="subtitle2" noWrap className="tile-name-short" sx={{
                      color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                      lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                      transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                    }}>
                      {user.email}
                    </Typography>

                    {/* Details revealed on hover */}
                    <Box className="tile-details" sx={{
                      opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                      display: 'flex', flexDirection: 'column', gap: 0.4, pt: 0.25, pb: 1, flex: 1,
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3, textShadow: '0 1px 3px rgba(0,0,0,0.4)' }} noWrap>
                        {fullName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', lineHeight: 1.2 }} noWrap>{user.email}</Typography>
                      </Box>
                      {user.mobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', lineHeight: 1.2 }}>{user.mobile}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Business sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', lineHeight: 1.2 }} noWrap>{companyName}</Typography>
                      </Box>

                      {/* Action buttons */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit">
                            <Button color="secondary" variant="contained" size="small"
                              onClick={(e) => { e.stopPropagation(); handleEdit(user); }}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                              <Edit fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <Button color="warning" variant="contained" size="small"
                              onClick={(e) => { e.stopPropagation(); handleResetPassword(user); }}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                              <LockReset fontSize="small" />
                            </Button>
                          </Tooltip>
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title={isSelf ? 'Cannot delete your own account' : 'Delete'}>
                            <span>
                              <Button color="error" variant="contained" size="small" disabled={isSelf}
                                onClick={(e) => { e.stopPropagation(); handleDelete(user); }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                <Delete fontSize="small" />
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Pagination info + Load More */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {paginatedUsers.length} of {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </Typography>
            {hasMoreTiles && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setTileVisibleCount(prev => prev + TILE_PAGE_SIZE)}
              >
                Load More
              </Button>
            )}
          </Box>
          </Box>
        )
      )}

      <UserDialog
        open={dialogOpen}
        onClose={closeDialog}
        user={currentUser}
        isEditing={isEditing}
        onSave={saveUser}
        saveLoading={saveLoading}
        companies={companies}
        onRefresh={() => setRefreshTrigger(prev => prev + 1)}
      />

      <UserViewDialog
        open={viewDialogOpen}
        onClose={() => { setViewDialogOpen(false); setUserToView(null); }}
        user={userToView}
        companies={companies}
      />

      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onClose={closeResetPasswordDialog}
        user={userToResetPassword}
        onReset={resetPassword}
        loading={resetPasswordLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            Are you sure you want to delete the user "{userToDelete?.email}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={closeDeleteDialog} 
            disabled={deleteLoading}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
